import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X, Settings } from 'lucide-react';
import { shouldShowCompatibilityWarning, detectBrowserCapabilities } from '../utils/browserCompat';
import { useGame } from '../context/GameContext';

const CompatibilityWarning: React.FC = () => {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const { actions } = useGame();

  useEffect(() => {
    // Check if we should show the warning
    if (shouldShowCompatibilityWarning() && !dismissed) {
      // Show warning after a brief delay to not interfere with game loading
      setTimeout(() => {
        setShow(true);
      }, 2000);
    }
  }, [dismissed]);

  const handleDismiss = () => {
    setShow(false);
    setDismissed(true);
    // Store dismissal in localStorage
    localStorage.setItem('pathways-compat-warning-dismissed', 'true');
  };

  const handleSwitchToTapMode = () => {
    actions.setDragMode(false);
    actions.setConfirmMode(true);
    handleDismiss();
  };

  // Check if already dismissed
  useEffect(() => {
    const wasDismissed = localStorage.getItem('pathways-compat-warning-dismissed') === 'true';
    if (wasDismissed) {
      setDismissed(true);
    }
  }, []);

  if (!show) return null;

  const capabilities = detectBrowserCapabilities();

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative"
          initial={{ scale: 0.8, y: 50 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.8, y: 50 }}
        >
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>

          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 bg-amber-100 rounded-full">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Compatibility Notice
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                We detected you're using an older iOS Safari browser
                {capabilities.safariVersion && ` (iOS ${capabilities.safariVersion})`}.
                For the best experience, we recommend using <strong>Tap Mode</strong> instead of drag gestures.
              </p>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4 mb-4">
            <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Recommended Settings
            </h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Use <strong>Tap Mode</strong> - click words to connect them</li>
              <li>• Enable <strong>Confirm Mode</strong> - prevents accidental connections</li>
              <li>• Consider updating your browser for better performance</li>
            </ul>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleSwitchToTapMode}
              className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Switch to Tap Mode
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-3 text-gray-600 hover:text-gray-800 font-medium transition-colors"
            >
              Keep Current
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CompatibilityWarning;